import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, UserPlus, Mail, Lock } from "lucide-react";
import { useAuth, isAdmin } from "@/lib/AuthContext";

export default function Accedi() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/area-riservata";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Benvenuto/a, ${u.first_name || u.name || u.email}`);
      // Admins → CMS, others → area riservata
      nav(isAdmin(u.role) ? "/admin" : from, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 pl-11 pr-4 py-3 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="accedi-page" className="min-h-[70vh]">
      <section className="bg-[#FFF1E8] py-14">
        <div className="container-vv">
          <div className="eyebrow">Area riservata</div>
          <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl tracking-tight text-[#292522]">Accedi</h1>
          <p className="mt-4 text-lg text-[#292522]/75 max-w-2xl">
            Entra nell'area riservata per gestire il tuo profilo, i tuoi eventi e — se sei socio/a — accedere all'archivio storico dell'associazione.
          </p>
        </div>
      </section>

      <section className="container-vv py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-4">
          <h2 className="font-display text-3xl font-medium">I ruoli previsti</h2>
          <ul className="space-y-2.5">
            <li className="rounded-2xl bg-[#F8F1E8] p-4"><strong>Utente</strong> · profilo base, prenotazione eventi</li>
            <li className="rounded-2xl bg-[#F8F1E8] p-4"><strong>Socio/a</strong> · verbali, convocazioni, archivio storico</li>
            <li className="rounded-2xl bg-[#F8F1E8] p-4"><strong>Collaboratore/trice</strong> · gestione progetti ed eventi</li>
            <li className="rounded-2xl bg-[#F8F1E8] p-4"><strong>Consiglio Direttivo</strong> · governance e verbali riservati</li>
            <li className="rounded-2xl bg-[#F8F1E8] p-4"><strong>Amministrazione</strong> · gestione completa del sito</li>
          </ul>
          <p className="text-sm text-[#292522]/70 mt-4">
            La registrazione crea un account <strong>Utente</strong>. L'amministrazione può poi promuoverti al ruolo appropriato.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="card-vv p-8 bg-white sticky top-28">
            <div className="h-14 w-14 rounded-2xl bg-[#FF6100]/12 flex items-center justify-center text-[#FF6100] mb-4">
              <LogIn className="h-7 w-7" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-2xl font-medium">Accedi</h3>
            <p className="mt-2 text-sm text-[#292522]/70">Email e password del tuo account.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold text-[#292522]/70">Email</span>
                <div className="relative mt-1">
                  <Mail className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#292522]/40" strokeWidth={1.75} />
                  <input required type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="mario.rossi@email.it" />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-[#292522]/70">Password</span>
                <div className="relative mt-1">
                  <Lock className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#292522]/40" strokeWidth={1.75} />
                  <input required type="password" className={input} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
                </div>
              </label>
              <div className="text-right">
                <Link to="/recupera-password" className="text-xs text-[#FF6100] hover:underline" data-testid="link-forgot-password">Password dimenticata?</Link>
              </div>
              <button disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
                <LogIn className="h-4 w-4" strokeWidth={1.75} />
                {loading ? "Accesso…" : "Entra"}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#292522]/8 text-sm">
              <p className="text-[#292522]/70">Non hai ancora un account?</p>
              <Link to="/registrati" className="btn-ghost mt-2 w-full justify-center" data-testid="link-register">
                <UserPlus className="h-4 w-4" strokeWidth={1.75} /> Registrati
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
